import React, { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import AuthShell, { GoogleButton } from "@/components/AuthShell";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

const Register = () => {
  const { user, register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const ref = params.get("ref");
    if (ref) localStorage.setItem("cr_ref", ref);
  }, [params]);

  useEffect(() => {
    if (user) navigate(user.role === "admin" ? "/admin" : user.onboarded ? "/dashboard" : "/onboarding", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(name, email, password, localStorage.getItem("cr_ref") || undefined);
      localStorage.removeItem("cr_ref");
      toast.success("Account created! Let's set up your growth.");
      navigate("/onboarding", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Sign up failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Create your account" subtitle="Start growing your TikTok in minutes."
      footer={<>Already have an account? <Link to="/login" className="text-primary font-semibold hover:underline">Log in</Link></>}>
      <GoogleButton label="Sign up with Google" />
      <div className="flex items-center gap-4 my-6">
        <div className="flex-1 h-px bg-white/10" /><span className="text-xs text-muted-foreground">or</span><div className="flex-1 h-px bg-white/10" />
      </div>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label htmlFor="name" className="text-sm">Name</Label>
          <Input id="name" required value={name} onChange={(e) => setName(e.target.value)}
            className="mt-1.5 h-11 bg-transparent border-white/10 focus:border-primary" placeholder="Your name" data-testid="register-name-input" />
        </div>
        <div>
          <Label htmlFor="email" className="text-sm">Email</Label>
          <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            className="mt-1.5 h-11 bg-transparent border-white/10 focus:border-primary" placeholder="you@example.com" data-testid="register-email-input" />
        </div>
        <div>
          <Label htmlFor="password" className="text-sm">Password</Label>
          <Input id="password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
            className="mt-1.5 h-11 bg-transparent border-white/10 focus:border-primary" placeholder="At least 6 characters" data-testid="register-password-input" />
        </div>
        <Button type="submit" disabled={loading} className="w-full h-11 rounded-full bg-primary text-background font-bold hover:bg-primary/90 transition-colors" data-testid="register-submit-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create account"}
        </Button>
      </form>
    </AuthShell>
  );
};

export default Register;
